/* Copyright 2026 WinSolutions. SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { type MatrixClient } from "matrix-js-sdk";

import { getHomeserverAssociatedDomain } from "../knownHomeserversMap";
import { type UseCallRecordingResult } from "../callrecorder";
import { type UseProtocolistRecordingResult } from "../protocolist";
import { prepareMeetingCapture, cancelMeetingCapture } from "./meetingCapture";
import styles from "./MeetingAutomation.module.css";

type Settings = { auto_protocolist: boolean; auto_recording: boolean };
export function useMeetingAutomation(
  client: MatrixClient,
  roomId: string,
  recorder?: UseCallRecordingResult,
  protocolist?: UseProtocolistRecordingResult,
): {
  controls: ReactNode;
  prepareEntry: () => void;
} {
  const [settings, setSettings] = useState<Settings>();
  const loadedRoom = useRef<string | undefined>(undefined);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  const protocolAttempted = useRef(false);
  const recordingAttempted = useRef(false);
  const request = async (value?: Settings): Promise<Settings | undefined> => {
    const token = (await client.getOpenIdToken()).access_token;
    const base = getHomeserverAssociatedDomain(
      client.getDomain() ?? "",
      "backend",
    );
    const response = await fetch(
      `https://${base}/vks/automation?room_id=${encodeURIComponent(roomId)}`,
      {
        method: value ? "PUT" : "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: value ? JSON.stringify(value) : undefined,
      },
    );
    if (!value && [403, 404].includes(response.status)) return undefined;
    if (!response.ok)
      throw new Error(
        "Не удалось сохранить или загрузить настройки ВКС. Повторите попытку.",
      );
    return response.json() as Promise<Settings>;
  };
  useEffect(() => {
    let alive = true;
    setSettings(undefined);
    loadedRoom.current = undefined;
    setError("");
    protocolAttempted.current = false;
    recordingAttempted.current = false;
    void request().then(
      (value) => {
        if (alive) {
          loadedRoom.current = roomId;
          setSettings(value);
        }
      },
      () => {
        if (alive) setError("Не удалось загрузить настройки ВКС.");
      },
    );
    return (): void => {
      alive = false;
    };
    // Settings belong to this authenticated organizer and this room.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, roomId, retry]);
  useEffect(() => {
    if (loadedRoom.current !== roomId) return;
    if (
      settings?.auto_protocolist &&
      protocolist &&
      !protocolAttempted.current
    ) {
      if (protocolist.viewState === "active") protocolAttempted.current = true;
      else if (
        protocolist.buttonState.kind === "start" &&
        !protocolist.buttonState.disabled
      ) {
        protocolAttempted.current = true;
        void protocolist
          .start()
          .catch(() =>
            setError(
              "Протоколист не запущен. Повторите запуск через меню встречи.",
            ),
          );
      }
    }
    if (settings?.auto_recording && recorder && !recordingAttempted.current) {
      recordingAttempted.current = true;
      if (
        !recorder.isLocalRecording &&
        !recorder.isLocalPreparing &&
        !recorder.localRecordingUserIds.has(client.getUserId() ?? "")
      ) {
        void recorder
          .startLocalRecording()
          .catch(() =>
            setError(
              "Запись не запущена. Разрешите захват окна через меню встречи.",
            ),
          );
      }
    }
  }, [settings, recorder, protocolist, client, roomId]);
  const change = (key: keyof Settings, enabled: boolean): void => {
    if (!settings || busy) return;
    if (
      key === "auto_recording" &&
      recorder &&
      enabled &&
      !recorder.isLocalRecording
    )
      prepareMeetingCapture(roomId);
    setBusy(true);
    setError("");
    void request({ ...settings, [key]: enabled })
      .then(async (value) => {
        setSettings(value);
        if (key === "auto_protocolist") {
          protocolAttempted.current = false;
          if (!enabled && protocolist?.buttonState.kind === "active_self_stop")
            await protocolist.stop();
        } else {
          recordingAttempted.current = false;
          if (!enabled) {
            cancelMeetingCapture(roomId);
            if (recorder?.isLocalRecording || recorder?.isLocalPreparing)
              await recorder.stopLocalRecording();
          }
        }
      })
      .catch((reason: unknown) => {
        if (key === "auto_recording") cancelMeetingCapture(roomId);
        setError(
          reason instanceof Error
            ? reason.message
            : "Не удалось изменить настройку ВКС.",
        );
      })
      .finally(() => setBusy(false));
  };
  const controls = (
    <>
      {settings && (
        <fieldset
          className={styles.settings}
          disabled={busy}
          aria-label="Автоматизация этой ВКС"
        >
          <legend>При входе организатора в эту ВКС</legend>
          <label>
            <input
              type="checkbox"
              checked={settings.auto_protocolist}
              onChange={(event) =>
                change("auto_protocolist", event.target.checked)
              }
            />
            Всегда приглашать протоколиста
          </label>
          <label>
            <input
              type="checkbox"
              checked={settings.auto_recording}
              onChange={(event) =>
                change("auto_recording", event.target.checked)
              }
            />
            Автоматически записывать окно встречи
          </label>
          <small>
            Записываются окно встречи, звук и демонстрация. При запросе системы
            выберите окно Клавы.
          </small>
        </fieldset>
      )}
      {!settings && error && (
        <button onClick={() => setRetry((value) => value + 1)}>
          Повторить загрузку настроек
        </button>
      )}
      {(error || recorder?.error) && (
        <p role="alert">{error || recorder?.error?.message}</p>
      )}
    </>
  );
  return {
    controls,
    prepareEntry: (): void => {
      if (settings?.auto_recording) prepareMeetingCapture(roomId);
    },
  };
}
